import React, { useState, useRef, useEffect } from "react";
import { X, Upload, Image as ImageIcon, CheckCircle2, AlertCircle, Trash2, Loader2 } from "lucide-react";
import ReactDOM from "react-dom";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const AddPhoto: React.FC<Props> = ({ isOpen, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
   const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleFileChange = (selectedFile: File) => {
    if (selectedFile && selectedFile.type.startsWith("image/")) {
      setFile(selectedFile);
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    }
  };

  const removeImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1200] flex items-start justify-center px-4 py-12 overflow-y-auto bg-slate-900/70 backdrop-blur-md">
      
      <div 
        ref={modalRef}
        className="relative bg-white w-full max-w-md rounded-xl shadow-[0_32px_72px_rgba(0,0,0,.22)] overflow-hidden animate-[gbpFadeIn_0.2s_ease]"
      >

        <div className="flex items-center gap-3.5 px-6 py-4 border-b border-slate-50 bg-white">
          <div className="w-9 h-9 rounded-[9px] flex items-center justify-center bg-blue-700 text-white">
            <Upload size={18} strokeWidth={2.5} />
          </div>
          <h2 className="m-0 text-[15px] font-bold text-slate-900 tracking-tight flex-1">Ajouter un média</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-100 text-slate-400 hover:text-slate-700 transition-all">
            <X size={15} />
          </button>
        </div>

        <div className="p-6">
          <div 
            onClick={() => !previewUrl && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const droppedFile = e.dataTransfer.files[0];
              handleFileChange(droppedFile);
            }}
            className={`
              relative group min-h-[220px] rounded-2xl border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center overflow-hidden
              ${isDragging ? "border-blue-500 bg-blue-50/50 scale-[1.01]" : "border-slate-200 bg-slate-50/50 hover:bg-white hover:border-blue-400"}
              ${previewUrl ? "border-none" : "cursor-pointer"}
            `}
          >
            {previewUrl ? (
              <div className="relative w-full h-full">
                <img src={previewUrl} alt="Preview" className="w-full h-[220px] object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                  <button 
                    onClick={removeImage}
                    className="p-2.5 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-xl hover:bg-red-500 hover:border-red-500 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider"
                  >
                    <Trash2 size={14} /> Supprimer
                  </button>
                </div>
                <div className="absolute top-2 left-2 px-2.5 py-1 bg-white/95 backdrop-blur-sm rounded-lg text-[9px] font-bold text-blue-700 uppercase flex items-center gap-1.5 shadow-sm">
                  <CheckCircle2 size={11} /> Prêt
                </div>
              </div>
            ) : (
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform text-slate-300 group-hover:text-blue-500">
                  <ImageIcon size={24} />
                </div>
                <p className="text-xs font-bold text-slate-700 m-0">Glissez votre photo ici</p>
                <p className="text-[10px] text-slate-400 m-0 mt-1">ou cliquez pour parcourir</p>
              </div>
            )}

            <input 
              ref={fileInputRef}
              type="file" 
              className="hidden" 
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && handleFileChange(e.target.files[0])}
            />
          </div>

          <div className="mt-5 flex items-start gap-3 p-3 bg-amber-50/50 rounded-xl border border-amber-100">
            <AlertCircle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-800 leading-relaxed font-medium m-0">
              Format JPG/PNG de min. 720px recommandé pour un affichage optimal sur Google Maps.
            </p>
          </div>
        </div>


        <div className="px-6 py-5 bg-slate-50/50 border-t border-slate-100 flex gap-3">
          <button 
            onClick={onClose}
            className="flex-1 py-3 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors"
          >
            Annuler
          </button>
          <button 
            disabled={!file }
            className={`
              flex-[2] py-3 rounded-xl text-xs font-bold shadow-lg transition-all flex items-center justify-center gap-2
              ${!file  
                ? "bg-slate-200 text-slate-400 cursor-not-allowed shadow-none" 
                : "bg-blue-700 text-white hover:bg-blue-700 shadow-blue-200 active:scale-95"}
            `}
          >
             <Loader2 size={16} className="animate-spin" /> : "Publier sur Google"
          </button>
        </div>
      </div>
    </div>,   
  document.body
  );
};

export default AddPhoto;